import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import type { RegistryStrategyResponse } from "@/shared/api";
import { readPlatformRegistry, readAccountRegistry } from "@/shared/api";
import { useSession } from "@/features/auth";

import { earlierCount, latestByStrategy } from "@/shared/strategy/registry";
import { StrategyCard } from "./StrategyCard";

/** The three pools every account trades, as the administrator set them. */
export function StrategiesPage(): React.JSX.Element {
  const session = useSession();
  const platform = session.status === "platform";
  const registryKey = ["strategy-registry", session.status,
    session.status === "signed-in" ? session.tenantId : null,
    session.status === "signed-in" ? session.accountId : null] as const;
  const query = useQuery({
    queryKey: registryKey,
    enabled: platform || session.status === "signed-in",
    retry: false,
    queryFn: async () => {
      const response = await (platform ? readPlatformRegistry() : readAccountRegistry());
      if (response.data === undefined) throw new Error("Registry unavailable");
      return response.data;
    },
  });
  const versions = query.data?.versions ?? [];
  const current = latestByStrategy(versions);
  const idle = !platform && query.data !== undefined
    && !query.data.assignments.some((item) => item.active_for_new_cycles);
  return <div className="flex min-w-0 flex-col gap-2" data-testid="registry-page">
    <PageHeader title="Стратегия"
      description="Три пула, по которым торгуют все счета. Параметры задаёт администратор." />
    {query.isPending ? <Panel>Загрузка…</Panel> : query.isError ?
      <Panel>Параметры стратегии недоступны — состояние неизвестно.</Panel> : <>
      {idle && <Panel testId="registry-assignments">
        <p className="text-body-main text-status-warning">Стратегия на счёт ещё не назначена — новые циклы не открываются.</p>
      </Panel>}
      {current.length === 0 && <Panel>Опубликованных стратегий пока нет.</Panel>}
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
        {current.map((version) => (
          <StrategyCard
            key={version.version_id}
            version={version}
            canRevise={session.status === "platform" && session.canPublish}
            registryKey={registryKey}
          />
        ))}
      </div>
      {platform && current.length > 0 &&
        <TechnicalDetails versions={current} earlier={(id) => earlierCount(versions, id)} />}
    </>}
  </div>;
}

/** Identities and hashes, for the administrator only, folded away. */
function TechnicalDetails({ versions, earlier }: {
  readonly versions: readonly RegistryStrategyResponse[];
  readonly earlier: (strategyId: string) => number;
}): React.JSX.Element {
  return <Panel testId="registry-technical">
    <details className="text-helper-sm text-text-muted">
      <summary className="cursor-pointer select-none text-body-main text-text-secondary">Технические данные</summary>
      <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {versions.map((version) => <dl key={version.version_id}
          className="grid min-w-0 gap-1 break-all font-mono text-data-mono">
          <dt className="font-sans text-body-main text-text-primary">{version.strategy_id}</dt><dd />
          <dt>Версия</dt><dd>{version.version_id}</dd>
          <dt>Версия стратегии</dt><dd>{version.strategy_version}</dd>
          <dt>Прошлых ревизий</dt><dd>{earlier(version.strategy_id)}</dd>
          <dt>Profile</dt><dd>{version.profile === null ? "—" : `${version.profile.profile_id} / ${version.profile.profile_version}`}</dd>
          <dt>Configuration</dt><dd>{version.configuration_sha256}</dd>
          <dt>Content</dt><dd>{version.content_sha256}</dd>
          {version.pool != null && <>
            <dt>Фильтр входа / оценка</dt><dd>{version.pool.guard_version} / {version.pool.score_policy_version}</dd>
            <dt>Контракт</dt><dd>{version.pool.strategy_contract_version} / {version.pool.strategy_contract_sha256}</dd>
          </>}
          {Object.entries(version.policies).map(([name, policy]) => (
            <div key={name} className="contents">
              <dt>{name}</dt><dd>{policy.version} / {policy.content_sha256}</dd>
            </div>
          ))}
        </dl>)}
      </div>
      <p className="mt-3">Открытые циклы остаются на своей версии.</p>
    </details>
  </Panel>;
}
